import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";

/**
 * Phase 25 — useUnsavedChangesGuard hook.
 *
 * Covers: GUARD25-01 (in-app navigation interceptor), GUARD25-02 (window.beforeunload listener).
 *
 * Two-layer lost-work protection for the page-based PlanEditor:
 *   1. In-app: `guardedNavigate(to)` either navigates immediately (when not dirty)
 *      or stashes `pendingNav` (when dirty) so the caller can render an
 *      AlertDialog that wires `confirmNav` + `cancelNav`.
 *   2. Browser: a `window.beforeunload` listener is attached while `isDirty`
 *      is true, surfacing the browser's native confirmation when the operator
 *      closes the tab / refreshes / hard-navigates.
 *
 * Browser back/forward buttons cannot be intercepted by either layer — this
 * is a known wouter v3 limitation. Documented in 25-HUMAN-UAT.md Walk 6.
 */
export interface UnsavedChangesGuardApi {
  guardedNavigate: (to: string) => void;
  pendingNav: string | null;
  confirmNav: () => void;
  cancelNav: () => void;
}

/**
 * Pure helper — decides whether `guardedNavigate` should intercept (stash
 * pendingNav) or pass through (navigate immediately).
 *
 * Exported for unit testing without React/DOM context (no jsdom in the
 * current vitest workspace — fallback documented in Plan 25-06 SUMMARY).
 */
export function shouldInterceptNavigation(isDirty: boolean): boolean {
  return isDirty === true;
}

/**
 * Pure helper — given the current pendingNav value and a confirm vs cancel
 * intent, returns the next (pendingNav, shouldNavigate, navigateTo) tuple.
 *
 * - confirm with pendingNav set → navigate to it, clear pendingNav.
 * - confirm with pendingNav null → no-op (clear stays null).
 * - cancel → clear pendingNav regardless.
 */
export type GuardTransitionResult = {
  nextPendingNav: string | null;
  shouldNavigate: boolean;
  navigateTo: string | null;
};

export function computeGuardTransition(
  pendingNav: string | null,
  intent: "confirm" | "cancel",
): GuardTransitionResult {
  if (intent === "cancel") {
    return { nextPendingNav: null, shouldNavigate: false, navigateTo: null };
  }
  // intent === 'confirm'
  if (pendingNav !== null) {
    return {
      nextPendingNav: null,
      shouldNavigate: true,
      navigateTo: pendingNav,
    };
  }
  return { nextPendingNav: null, shouldNavigate: false, navigateTo: null };
}

export function useUnsavedChangesGuard(
  isDirty: boolean,
): UnsavedChangesGuardApi {
  const [, setLocation] = useLocation();
  const [pendingNav, setPendingNav] = useState<string | null>(null);

  const guardedNavigate = useCallback(
    (to: string) => {
      if (shouldInterceptNavigation(isDirty)) {
        setPendingNav(to);
      } else {
        setLocation(to);
      }
    },
    [isDirty, setLocation],
  );

  const confirmNav = useCallback(() => {
    const result = computeGuardTransition(pendingNav, "confirm");
    if (result.shouldNavigate && result.navigateTo !== null) {
      setLocation(result.navigateTo);
    }
    setPendingNav(result.nextPendingNav);
  }, [pendingNav, setLocation]);

  const cancelNav = useCallback(() => {
    setPendingNav(null);
  }, []);

  // GUARD25-02 — attach window.beforeunload while dirty, detach when clean.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Most browsers ignore custom strings now and show their own copy,
      // but assigning returnValue is still the contract for triggering the
      // confirmation dialog. Empty string is the canonical no-op message.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  return { guardedNavigate, pendingNav, confirmNav, cancelNav };
}
